import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { Spin } from 'antd';

const CATEGORY_COLORS = ['#00A8FF', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#FF66C3', '#4bc0c8', '#ff9f40'];

const formatPercentage = (percentage) => {
    if (typeof percentage === 'number' && !isNaN(percentage)) {
        return `${percentage.toFixed(2)}%`;
    }
    return '0.00%';
};

const Category = ({ userData, analyticsData, loading, activeCategory, onCategorySelect }) => {

    const [localSelectedCategory, setLocalSelectedCategory] = useState(null);
    const selectedCategory = activeCategory !== undefined ? activeCategory : localSelectedCategory;

    const chartRef = useRef(null);       // DOM node
    const chartInstance = useRef(null);  // ApexCharts instance
    const lastSelectedIdxRef = useRef(null);
    const isMounted = useRef(true);
    const isUpdatingSelection = useRef(false);
    const renderTimeoutRef = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => { 
            isMounted.current = false;
            // Clear timeout on unmount
            if (renderTimeoutRef.current) {
                clearTimeout(renderTimeoutRef.current);
            }
            // Destroy chart instance on unmount
            if (chartInstance.current) {
                try { 
                    chartInstance.current.destroy(); 
                } catch (_) {}
                chartInstance.current = null;
            }
        };
    }, []);

    const setSelected = useCallback((nextValue) => {
        if (!isMounted.current) return;
        onCategorySelect?.(nextValue);
        if (activeCategory === undefined) {
            setLocalSelectedCategory(nextValue);
        }
    }, [activeCategory, onCategorySelect]);

    const chartSeries = useMemo(() =>
            analyticsData?.alerts_by_categories?.map(item => item.count) || [],
        [analyticsData?.alerts_by_categories]
    );

    const chartLabels = useMemo(() =>
            analyticsData?.alerts_by_categories?.map(item => item.category) || [],
        [analyticsData?.alerts_by_categories]
    );

    const totalAlerts = analyticsData?.total_alerts || 0;

    const handleCategoryClick = useCallback((categoryName) => {
        if (!isMounted.current || isUpdatingSelection.current) return;
        
        const next = selectedCategory === categoryName ? null : categoryName;
        setSelected(next);
    }, [selectedCategory, setSelected]);

    // Build chart options
    const buildOptions = useCallback((labels, total) => ({
        chart: {
            type: 'donut',
            width: '100%',
            events: {
                dataPointSelection: (_event, _chartContext, config) => {
                    if (!isMounted.current || isUpdatingSelection.current) return;
                    const idx = config?.dataPointIndex;
                    const label = typeof idx === 'number' && labels?.[idx] ? labels[idx] : null;
                    if (label && label !== 'No Data') {
                        handleCategoryClick(label);
                    }
                },
            },
        },
        labels: labels.length > 0 ? labels : ['No Data'],
        colors: CATEGORY_COLORS,
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'Total Alerts',
                            fontSize: '14px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            color: '#373d3f',
                            formatter: () => total?.toString() || '0',
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        responsive: [{
            breakpoint: 100,
            options: { chart: { width: 70 }, legend: { show: false } }
        }]
    }), [handleCategoryClick]);

    // Initialize or update chart
    useEffect(() => {
        // Don't proceed if loading, no data, or component unmounted
        if (loading || !isMounted.current || chartSeries.length === 0) return;
        
        // Small delay to ensure DOM is ready
        if (renderTimeoutRef.current) {
            clearTimeout(renderTimeoutRef.current);
        }
        
        renderTimeoutRef.current = setTimeout(() => {
            if (!isMounted.current || !chartRef.current) return;
            
            const initChart = async () => {
                try {
                    // Destroy existing instance if any
                    if (chartInstance.current) {
                        await chartInstance.current.destroy();
                        chartInstance.current = null;
                    }
                    
                    // Create new chart instance
                    const options = buildOptions(chartLabels, totalAlerts);
                    const chart = new ApexCharts(chartRef.current, { ...options, series: chartSeries });
                    await chart.render();
                    
                    if (isMounted.current) {
                        chartInstance.current = chart;
                        lastSelectedIdxRef.current = null;
                    } else if (chart) {
                        // Cleanup if component unmounted during render
                        await chart.destroy();
                    }
                } catch (error) {
                    console.warn('Chart initialization error:', error);
                    if (error.message?.includes('querySelectorAll')) {
                        // Retry after a delay if DOM not ready
                        if (isMounted.current) {
                            renderTimeoutRef.current = setTimeout(initChart, 100);
                        }
                    }
                }
            };
            
            initChart();
        }, 50);
        
        return () => {
            if (renderTimeoutRef.current) {
                clearTimeout(renderTimeoutRef.current);
            }
        };
    }, [chartSeries, chartLabels, totalAlerts, loading, buildOptions]);

    // Update chart data when series/labels change (without recreating)
    useEffect(() => {
        if (!chartInstance.current || loading || !isMounted.current || chartSeries.length === 0) return;
        
        const updateChart = async () => {
            try {
                // Check if chart instance is still valid
                if (!chartInstance.current || !chartInstance.current.w) {
                    return;
                }
                
                // Update series
                await chartInstance.current.updateSeries(chartSeries);
                
                // Update labels if changed
                const currentLabels = chartInstance.current.w.config.labels;
                if (JSON.stringify(currentLabels) !== JSON.stringify(chartLabels)) {
                    await chartInstance.current.updateOptions({
                        labels: chartLabels.length > 0 ? chartLabels : ['No Data'],
                        plotOptions: {
                            pie: {
                                donut: {
                                    labels: {
                                        total: {
                                            formatter: () => totalAlerts.toString()
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            } catch (error) {
                console.warn('Chart update error:', error);
                // If update fails, try to recreate chart
                if (isMounted.current && chartRef.current) {
                    try {
                        if (chartInstance.current) {
                            await chartInstance.current.destroy();
                            chartInstance.current = null;
                        }
                        const options = buildOptions(chartLabels, totalAlerts);
                        const chart = new ApexCharts(chartRef.current, { ...options, series: chartSeries });
                        await chart.render();
                        if (isMounted.current) {
                            chartInstance.current = chart;
                        }
                    } catch (recreateError) {
                        console.warn('Chart recreation error:', recreateError);
                    }
                }
            }
        };
        
        updateChart();
    }, [chartSeries, chartLabels, totalAlerts, loading, buildOptions]);

    // Sync selected category highlight with chart
    useEffect(() => {
        if (!chartInstance.current || !chartLabels?.length || loading || !isMounted.current) return;
        
        const syncSelection = async () => {
            try {
                isUpdatingSelection.current = true;
                
                if (!selectedCategory) {
                    if (lastSelectedIdxRef.current !== null && chartInstance.current.clearSelection) {
                        chartInstance.current.clearSelection();
                        lastSelectedIdxRef.current = null;
                    }
                    return;
                }

                const nextIdx = chartLabels.indexOf(selectedCategory);
                if (nextIdx >= 0 && lastSelectedIdxRef.current !== nextIdx) {
                    if (chartInstance.current.clearSelection) {
                        chartInstance.current.clearSelection();
                    }
                    if (chartInstance.current.toggleDataPointSelection) {
                        chartInstance.current.toggleDataPointSelection(0, nextIdx);
                    }
                    lastSelectedIdxRef.current = nextIdx;
                }
            } catch (error) {
                console.warn('Selection sync error:', error);
            } finally {
                setTimeout(() => {
                    if (isMounted.current) {
                        isUpdatingSelection.current = false;
                    }
                }, 100);
            }
        };
        
        syncSelection();
    }, [chartLabels, selectedCategory, loading]);

    // Show loading state
    // if (loading) {
    //     return (
    //         <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', width: '100%' }}>
    //             <Spin size="large" tip="Loading categories..." />
    //         </div>
    //     );
    // }

    return (
        <>
            <div className="row g-5">
                <div className="col-md-6 col-12 ">
                    <div id="chart" className="w-100 d-flex justify-content-center">
                        <div style={{ width: '100%', maxWidth: 420, minHeight: 330 }}>
                            {chartSeries.length > 0 ? (
                                <div ref={chartRef} />
                            ) : (
                                <div
                                    className="w-100 d-flex align-items-center justify-content-center text-gray-500"
                                    style={{ height: 330 }}
                                >
                                    No category data available.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-5 mx-auto col-12">
                    <p className="fs-4 fw-bold mb-7">Alerts by categories</p>
                    <div className="d-flex flex-column content-justify-center w-100">
                        {analyticsData?.alerts_by_categories?.map((item, index) => {
                            const isSelected = item.category === selectedCategory;
                            return (
                                <div
                                    key={item.category}
                                    className={`d-flex fs-6 fw-bold align-items-center mb-4 p-3 border rounded ${isSelected ? 'bg-light-danger border-danger' : ''}`}
                                    onClick={() => handleCategoryClick(item.category)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div
                                        className="bullet w-8px h-8px rounded-2 me-3"
                                        style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                                    />
                                    <div className={`flex-grow-1 me-4 ${isSelected ? 'text-danger fw-bolder' : 'text-gray-800'}`}>{item.category}</div>
                                    <div className="fw-semibold fs-6 text-gray-700 text-xxl-end me-4">
                                        {formatPercentage(item.percentage)}
                                    </div>
                                    <div className="fw-bolder fs-5 text-gray-800 text-xxl-end min-w-100px ">
                                        {item.count}
                                    </div>
                                </div>
                            );
                        })}
                        {(!analyticsData?.alerts_by_categories || analyticsData.alerts_by_categories.length === 0) && (
                            <div className='text-gray-500'>No categories found.</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Category;